"use client";
import React, { useCallback, useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import EventFilterSheet from "./EventFilterSheet";
import {
  getEventCityDisplayLabel,
  getEventCountryDisplayLabel,
  getEventCountryValue,
} from "./eventGeoLabels";
import {
  DEFAULT_EVENT_DISCOVERY_RADIUS_KM,
  EVENT_DISCOVERY_RADIUS_OPTIONS_KM,
  getEventDiscoveryLocationParams,
  normalizeOptionalEventDiscoveryRadiusKm,
} from "../../utils/eventDiscoveryRadius";
import { normalizeSelectedEventCity } from "../../utils/eventCitySelection";
import { normalizeMunicipalityId } from "../../utils/eventLocationFilterStorage";

const clearButtonVariants = {
  hidden: {
    opacity: 0,
    width: 0,
    marginLeft: 0,
    scale: 0.96,
  },
  visible: {
    opacity: 1,
    width: 132,
    marginLeft: 8,
    scale: 1,
    transition: { duration: 0.24, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    width: 0,
    marginLeft: 0,
    scale: 0.96,
    transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
  },
};

const buildCityOptions = (cities, allCitiesLabel, getCityLabel = (city) => city, country) => {
  const uniq = new Map();
  const registryNames = new Set((Array.isArray(cities) ? cities : [])
    .filter((raw) => raw && typeof raw === "object" && normalizeMunicipalityId(raw.municipality_id))
    .map((raw) => normalizeSelectedEventCity(country, raw.name).toLocaleLowerCase()));

  (Array.isArray(cities) ? cities : []).forEach((raw) => {
    const city = normalizeSelectedEventCity(country, typeof raw === "string" ? raw : raw?.name);
    if (!city) return;
    const municipalityId = normalizeMunicipalityId(raw?.municipality_id);
    if (!municipalityId && registryNames.has(city.toLocaleLowerCase())) return;
    const key = municipalityId ? `municipality:${municipalityId}` : city.toLocaleLowerCase();
    if (!uniq.has(key)) uniq.set(key, {
      value: city,
      municipalityId,
      label: getCityLabel(city),
      featured: Boolean(raw?.is_suggested_city),
    });
  });

  const cityOptions = [...uniq.values()]
    .sort((a, b) => Number(b.featured) - Number(a.featured) || a.label.localeCompare(b.label));

  return [{ value: "", label: allCitiesLabel, municipalityId: null }, ...cityOptions];
};

function EventGeoFilter({
  expandedHeader = false,
  onClose,
  selectedCountry,
  selectedCity,
  selectedMunicipalityId = null,
  selectedRadiusKm = DEFAULT_EVENT_DISCOVERY_RADIUS_KM,
  geoCount,
  onLocationChange,
  onApply,
  setGeoCount,
  marketRegion = "EU",
  // include other filters from props if needed: viewType, selectedCategories, selectedDate, searchQuery
}) {
  const { t } = useTranslation("events");
  const [openCity, setOpenCity] = useState(false);

  const countryValue = getEventCountryValue(marketRegion, selectedCountry);
  const [tempCountry, setTempCountry] = useState(countryValue);
  const [tempCity, setTempCity] = useState(() => normalizeSelectedEventCity(countryValue, selectedCity));
  const [tempMunicipalityId, setTempMunicipalityId] = useState(() => normalizeMunicipalityId(selectedMunicipalityId));
  const [tempRadiusKm, setTempRadiusKm] = useState(
    normalizeOptionalEventDiscoveryRadiusKm(selectedRadiusKm)
  );
  const [tempCount, setTempCount] = useState(geoCount);
  const allCitiesLabel = t("filters.geo.all_cities");
  const countryDisplayLabel = getEventCountryDisplayLabel(t, marketRegion, selectedCountry);
  const getCityLabel = useCallback((city) => getEventCityDisplayLabel(t, city, marketRegion), [t, marketRegion]);
  const [cityOptions, setCityOptions] = useState(() => [
    { value: "", label: allCitiesLabel },
  ]);
  const hasActiveFilter = Boolean(tempCity);

  useEffect(() => {
    // Normalize incoming props so that region/city combinations are always valid.
    const nextCountry = countryValue;
    const nextCity = normalizeSelectedEventCity(nextCountry, selectedCity);

    setTempCountry(nextCountry);
    setTempCity(nextCity);
    setTempMunicipalityId(normalizeMunicipalityId(selectedMunicipalityId));
    setTempRadiusKm(normalizeOptionalEventDiscoveryRadiusKm(selectedRadiusKm));
    setTempCount(geoCount);
  }, [countryValue, selectedCountry, selectedCity, selectedMunicipalityId, selectedRadiusKm, geoCount]);

  useEffect(() => {
    let cancelled = false;

    const applyCities = (cities) => {
      const nextOptions = buildCityOptions(cities, allCitiesLabel, getCityLabel, tempCountry);
      setCityOptions(nextOptions);

      const hasCurrent = nextOptions.some((opt) => tempMunicipalityId
        ? opt.municipalityId === tempMunicipalityId
        : String(opt.value).toLowerCase() === String(tempCity || "").toLowerCase());
      if (!hasCurrent) {
        setTempCity(nextOptions[0].value);
        setTempMunicipalityId(null);
        setTempCount(null);
      }
    };

    api
      .get("/events/available-cities/", {
        params: { country: tempCountry },
        authOptional: true,
        suppressUnauthorizedRedirect: true,
      })
      .then(({ data }) => {
        if (cancelled) return;
        applyCities([
          ...(Array.isArray(data?.municipality_options) ? data.municipality_options : []),
          ...(Array.isArray(data?.cities) ? data.cities : []),
        ]);
      })
      .catch(() => {
        if (cancelled) return;
        // A temporary catalogue failure must not silently discard the member's
        // already selected city or its verified municipality ID.
        setCityOptions(buildCityOptions(
          tempCity ? [{ name: tempCity, municipality_id: tempMunicipalityId }] : [],
          allCitiesLabel,
          getCityLabel,
          tempCountry
        ));
      });

    return () => {
      cancelled = true;
    };
  }, [allCitiesLabel, getCityLabel, tempCountry, tempCity, tempMunicipalityId]);

  useEffect(() => {
    if (tempCount != null) return;

    let cancelled = false;
    api
      .get("/events/events_packs/", {
        params: {
          country: tempCountry,
          count_only: 1,
          ...(tempCity ? { city: tempCity } : {}),
          ...(tempCity && tempMunicipalityId ? { municipality_id: tempMunicipalityId } : {}),
          ...(tempCity
            ? getEventDiscoveryLocationParams(tempRadiusKm)
            : {}),
        },
        authOptional: true,
        suppressUnauthorizedRedirect: true,
      })
      .then(({ data }) => {
        if (!cancelled && typeof data.count === "number") {
          setTempCount(data.count);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [tempCity, tempCountry, tempCount, tempMunicipalityId, tempRadiusKm]);

  const fetchCount = (country, city, radiusKm = tempRadiusKm, municipalityId = tempMunicipalityId) => {
    setTempCount(null);
    api
      .get("/events/events_packs/", {
        params: {
          country,
          count_only: 1,
          ...(city ? { city } : {}),
          ...(city && municipalityId ? { municipality_id: municipalityId } : {}),
          ...(city
            ? getEventDiscoveryLocationParams(radiusKm)
            : {}),
        },
        authOptional: true,
        suppressUnauthorizedRedirect: true,
      })
      .then(({ data }) => {
        if (typeof data.count === "number") {
          setTempCount(data.count);
        }
      })
      .catch(() => {});
  };

  const handleClear = () => {
    setTempCountry(countryValue);
    setTempCity("");
    setTempMunicipalityId(null);
    setTempRadiusKm(DEFAULT_EVENT_DISCOVERY_RADIUS_KM);
    setOpenCity(false);
    fetchCount(countryValue, "", DEFAULT_EVENT_DISCOVERY_RADIUS_KM, null);
  };

  const handleApply = (closeSheet) => {
    onLocationChange(tempCountry, tempCity, tempRadiusKm, tempMunicipalityId);
    setGeoCount(tempCount);
    closeSheet(onApply);
  };

  return (
    <EventFilterSheet
      expandedHeader={expandedHeader}
      onClose={onClose}
      closeLabel={t("filters.geo.header.close_aria")}
      footer={({ closeSheet }) => (
        <div className="flex w-full items-center">
          <motion.button
            type="button"
            layout
            className={`h-[55px] w-full rounded-[100px] border border-white/20 text-[15px] font-semibold leading-4 ${
              tempCount != null
                ? "bg-white text-[#3E6FFB]"
                : "bg-white/30 text-white/45 cursor-not-allowed"
            }`}
            disabled={tempCount == null}
            onClick={() => handleApply(closeSheet)}
          >
            {tempCount != null
              ? t("filters.geo.btn_show_events_count", { count: tempCount })
              : t("filters.geo.btn_show_events")}
          </motion.button>
          <AnimatePresence initial={false}>
            {hasActiveFilter ? (
              <motion.button
                key="clear-filters"
                type="button"
                variants={clearButtonVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="h-[55px] shrink-0 overflow-hidden rounded-[100px] border border-white/20 bg-white/12 text-[14px] font-semibold leading-4 text-white/75 active:scale-[0.98]"
                onClick={handleClear}
              >
                <span className="block px-3">
                  {t("filters.geo.btn_clear_filters", { defaultValue: "Clear filters" })}
                </span>
              </motion.button>
            ) : null}
          </AnimatePresence>
        </div>
      )}
    >
      <section>
        {/* Country is fixed by the EU/RU application contour. */}
        <div className="mb-[30px] relative">
          <label className="mb-[15px] block text-[24px] font-semibold leading-[22px] tracking-[-0.4px] text-white">
            {t("filters.geo.country_label", { defaultValue: "Country" })}
          </label>
          <div className="flex h-[50px] w-full items-center rounded-[25px] border border-white/25 bg-black/10 px-[20px] text-[15px] font-normal leading-[22px] tracking-[-0.4px] text-white/75">
            {countryDisplayLabel}
          </div>
        </div>

        {/* City */}
        <div className="relative">
          <label className="mb-[15px] block text-[24px] font-semibold leading-[22px] tracking-[-0.4px] text-white">
            {t("filters.geo.city_label")}
          </label>
          <div className="relative w-full">
            <div
              className={`overflow-hidden rounded-[25px] border border-white/25 bg-black/10 transition-all duration-200 ${
                openCity ? "max-h-[250px]" : "max-h-[50px]"
              }`}
            >
              <button
                type="button"
                onClick={() => setOpenCity((prev) => !prev)}
                className="flex h-[50px] w-full items-center justify-between px-[20px] text-left text-[15px] font-normal leading-[22px] tracking-[-0.4px] text-white/75"
              >
                <span>{tempCity
                  ? cityOptions.find((option) => option.municipalityId === tempMunicipalityId && option.value === tempCity)?.label || getCityLabel(tempCity)
                  : allCitiesLabel}</span>
                <img
                  src="/assets/events/down_arrow.svg"
                  alt={t("filters.geo.arrow_alt")}
                  className={`h-4 w-4 transition-transform ${
                    openCity ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openCity && (
                <div className="max-h-[200px] overflow-y-auto border-t border-white/10 bg-black/10 py-1 custom-scrollbar">
                  {cityOptions.map((opt) => (
                    <button
                      type="button"
                      key={opt.municipalityId || opt.value}
                      onClick={() => {
                        const nextRadiusKm = opt.value !== tempCity || opt.municipalityId !== tempMunicipalityId
                          ? DEFAULT_EVENT_DISCOVERY_RADIUS_KM
                          : tempRadiusKm;
                        setTempCity(opt.value);
                        setTempMunicipalityId(opt.municipalityId);
                        setTempRadiusKm(nextRadiusKm);
                        setOpenCity(false);
                        fetchCount(tempCountry, opt.value, nextRadiusKm, opt.municipalityId);
                      }}
                      className="w-full px-[20px] py-2 text-left text-[15px] text-white/80 hover:bg-white/10"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {tempCity ? (
          <div className="mt-[30px]">
            <label className="mb-[6px] block text-[24px] font-semibold leading-[26px] tracking-[-0.4px] text-white">
              {t("filters.geo.radius_label", { defaultValue: "Distance" })}
            </label>
            <p className="mb-[14px] text-[13px] leading-[18px] text-white/55">
              {t("filters.geo.radius_hint", {
                defaultValue: "Events for this city are shown by default. Tap the selected distance again to turn it off.",
              })}
            </p>
            <div className="grid grid-cols-3 gap-[8px]">
              {EVENT_DISCOVERY_RADIUS_OPTIONS_KM.filter((radiusKm) => radiusKm > 0).map((radiusKm) => {
                const active = tempRadiusKm === radiusKm;
                return (
                  <button
                    key={radiusKm}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      const nextRadiusKm = active ? null : radiusKm;
                      setTempRadiusKm(nextRadiusKm);
                      fetchCount(tempCountry, tempCity, nextRadiusKm, tempMunicipalityId);
                    }}
                    className={`h-[48px] rounded-[20px] border text-[15px] font-semibold transition active:scale-[0.98] ${
                      active
                        ? "border-white bg-white text-[#3E6FFB]"
                        : "border-white/25 bg-black/10 text-white/75"
                    }`}
                  >
                    {`${t("filters.geo.nearby", { defaultValue: "Within" })} ${radiusKm} km`}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </section>
    </EventFilterSheet>
  );
}

export default EventGeoFilter;
